/**
 * Data Completeness Service
 * ---------------------------------------------------------------------------
 * Maqsad: jab kisi hotel ka API data adhura ho (tax, cancellation policy,
 * photos, room size, amenities, etc. missing), use DETECT karo aur backend ko
 * REPORT karo — taake team source pe hotel profile complete kare.
 *
 * Philosophy: fake fallback = problem chhupana (customer ko jhooti info).
 * Completeness reporting = problem expose karna → source pe fix.
 *
 * Yeh module:
 *   1. checkHotelCompleteness(rooms, context) → pure function, missing fields + score
 *   2. reportCompleteness(report) → backend ko bhejta hai (env-gated, fail-silent,
 *      per-hotel-per-session dedupe). Endpoint na ho to dev console pe log.
 *
 * Backend/dashboard contract: docs/DATA-COMPLETENESS-SPEC.md
 */

// Endpoint env se (jab tak set na ho, koi network call nahi — sirf dev console log).
const COMPLETENESS_ENDPOINT = process.env.REACT_APP_COMPLETENESS_ENDPOINT || '';
const IS_DEV = process.env.NODE_ENV === 'development';

/**
 * Required-fields schema = "complete hotel" ka contract.
 * level: 'critical' (booking ke liye zaruri / legal) | 'important' (UX/conversion) | 'optional' (nice-to-have)
 * scope: 'room' (har room) | 'ratePlan' (har rate plan) | 'hotel' (hotel-level)
 */
export const COMPLETENESS_SCHEMA = {
  hotel: [
    { key: 'name', level: 'critical', label: 'Hotel name' },
  ],
  room: [
    { key: 'name', level: 'critical', label: 'Room name' },
    { key: 'images', level: 'important', label: 'Room photos', minCount: 1 },
    { key: 'imagesMultiple', level: 'optional', label: 'Multiple photos (3+)', minCount: 3, source: 'images' },
    { key: 'size', level: 'important', label: 'Room size' },
    { key: 'guests', level: 'important', label: 'Max occupancy' },
    { key: 'amenities', level: 'important', label: 'Amenities', minCount: 1 },
    { key: 'BedSize', level: 'optional', label: 'Bed type' },
    { key: 'description', level: 'optional', label: 'Room description' },
  ],
  ratePlan: [
    { key: 'price', level: 'critical', label: 'Rate / price' },
    { key: 'taxes', level: 'critical', label: 'Taxes', minCount: 1 },
    { key: 'cancellation', level: 'important', label: 'Cancellation policy' },
    { key: 'refundStatus', level: 'important', label: 'Refundable status' },
    { key: 'breakFastTypeName', level: 'optional', label: 'Breakfast info' },
  ],
};

// Weights for completeness score (critical sabse zyada matter karta hai)
const LEVEL_WEIGHT = { critical: 5, important: 2, optional: 1 };

/** Ek value "present" hai ya nahi — string/array/number/null sab handle */
const isPresent = (val, rule) => {
  if (val === null || val === undefined || val === '') return false;
  if (Array.isArray(val)) return val.length >= (rule.minCount || 1);
  if (typeof val === 'string') return val.trim() !== '' && val.trim().toLowerCase() !== 'n/a';
  if (typeof val === 'number') return !isNaN(val) && val > 0;
  return true;
};

/** 'size' jaisa field "N/A" / "0 sq. ft." ho to missing maano */
const getFieldValue = (obj, rule) => {
  const sourceKey = rule.source || rule.key;
  const val = obj[sourceKey];
  if (rule.key === 'size' && typeof val === 'string' && /n\/?a|^0\b/i.test(val.trim())) return '';
  return val;
};

/**
 * Hotel ka completeness check karo — PURE function (koi side effect nahi).
 * @param {Array} rooms - transformApiRoomsToLocalFormat ka output
 * @param {Object} context - { hotelId, hotelName }
 * @returns {Object} { hotelId, hotelName, score, counts, missing[], roomsChecked }
 */
export const checkHotelCompleteness = (rooms = [], context = {}) => {
  const { hotelId = '', hotelName = '' } = context;
  const missing = [];
  let earned = 0;
  let possible = 0;

  const score1 = (rule, present, scope, ref) => {
    const w = LEVEL_WEIGHT[rule.level] || 1;
    possible += w;
    if (present) earned += w;
    else missing.push({ scope, ref: ref || '', field: rule.key, label: rule.label, level: rule.level });
  };

  // Hotel-level
  COMPLETENESS_SCHEMA.hotel.forEach((rule) => {
    const present = isPresent(rule.key === 'name' ? hotelName : context[rule.key], rule);
    score1(rule, present, 'hotel', hotelName);
  });

  // Per room + per rate plan
  (rooms || []).forEach((room) => {
    const roomRef = room.name || room.RoomName || `room#${room.id}`;
    COMPLETENESS_SCHEMA.room.forEach((rule) => {
      score1(rule, isPresent(getFieldValue(room, rule), rule), 'room', roomRef);
    });
    (room.ratePlans || []).forEach((rp) => {
      const rpRef = `${roomRef} › ${rp.name || rp.RatePlanName || 'rate'}`;
      COMPLETENESS_SCHEMA.ratePlan.forEach((rule) => {
        score1(rule, isPresent(getFieldValue(rp, rule), rule), 'ratePlan', rpRef);
      });
    });
  });

  const counts = missing.reduce(
    (acc, m) => { acc[m.level] = (acc[m.level] || 0) + 1; return acc; },
    { critical: 0, important: 0, optional: 0 }
  );

  const score = possible > 0 ? Math.round((earned / possible) * 100) : 0;

  return {
    hotelId: String(hotelId),
    hotelName,
    score,            // 0-100
    counts,           // { critical, important, optional }
    missing,          // [{ scope, ref, field, label, level }]
    roomsChecked: (rooms || []).length,
  };
};

/**
 * Completeness report backend ko bhejo.
 * - Env endpoint na ho → sirf dev console (network call NAHI). Prod mein inert jab tak configure na ho.
 * - Per hotel per session sirf 1 baar (dedupe).
 * - Fail-silent + non-blocking (keepalive) — page kabhi na ruke.
 * @param {Object} report - checkHotelCompleteness ka output
 */
export const reportCompleteness = (report) => {
  try {
    if (!report || !report.hotelId) return;

    // Sab complete hai (koi missing nahi) → report ki zarurat nahi
    if (!report.missing || report.missing.length === 0) return;

    // Dedupe — ek hotel ek session mein 1 baar
    const dedupeKey = `completenessReported:${report.hotelId}`;
    try {
      if (sessionStorage.getItem(dedupeKey)) return;
      sessionStorage.setItem(dedupeKey, '1');
    } catch (e) { /* sessionStorage blocked — chalo aage */ }

    const payload = {
      ...report,
      checkedAt: new Date().toISOString(),
      url: typeof window !== 'undefined' ? window.location.href : '',
      device: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
    };

    // Dev: hamesha console pe dikhao (turant visibility, bina backend ke)
    if (IS_DEV) {
      const tag = `%c[Data Completeness] ${report.hotelName || report.hotelId} — ${report.score}% (critical:${report.counts.critical}, important:${report.counts.important})`;
      // eslint-disable-next-line no-console
      console.warn(tag, 'color:#9b2136;font-weight:bold', payload.missing);
    }

    // Endpoint set hai to backend ko bhejo (dashboard isse read karega)
    if (COMPLETENESS_ENDPOINT) {
      fetch(COMPLETENESS_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true, // page unload pe bhi complete ho
      }).catch(() => { /* fail-silent */ });
    }
  } catch (e) {
    /* completeness reporting kabhi page na tode */
  }
};

/** Convenience: check + report ek saath (components isse call karein) */
export const auditAndReportCompleteness = (rooms, context) => {
  try {
    const report = checkHotelCompleteness(rooms, context);
    reportCompleteness(report);
    return report;
  } catch (e) {
    return null;
  }
};
